import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import axios from 'helpers/api';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import paths from 'routes/paths';
import LoginSourceBadge from './LoginSourceBadge';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';

const REFRESH_INTERVAL = 30000;

const ROLE_MAP = {
    1: { label: 'System Admin', bg: 'danger' },
    2: { label: 'Pharmacy Admin', bg: 'primary' },
    3: { label: 'Pharmacy Staff', bg: 'info' },
    4: { label: 'Group Admin', bg: 'warning' },
    5: { label: 'Facility Staff', bg: 'secondary' },
};

const ROLE_OPTIONS = [
    { label: 'All Roles', value: '' },
    { label: 'Pharmacy Admin', value: 2 },
    { label: 'Pharmacy Staff', value: 3 },
    { label: 'Group Admin', value: 4 },
    { label: 'Facility Staff', value: 5 },
];

const SOURCE_OPTIONS = [
    { label: 'All Sources', value: '' },
    { label: 'Web', value: 1 },
    { label: 'Android', value: 2 },
    { label: 'iOS', value: 3 },
];

const IDLE_OPTIONS = [
    { label: 'Any idle time', value: '' },
    { label: '> 5 min idle', value: 5 },
    { label: '> 15 min idle', value: 15 },
    { label: '> 30 min idle', value: 30 },
    { label: '> 60 min idle', value: 60 },
];

const SocketDot = ({ connected }) => (
    <span
        title={connected ? 'Connected' : 'Disconnected'}
        style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: connected ? '#28a745' : '#dc3545',
            boxShadow: connected
                ? '0 0 4px 1px rgba(40,167,69,0.6)'
                : '0 0 4px 1px rgba(220,53,69,0.4)',
        }}
    />
);

const RoleBadge = ({ roleId }) => {
    const config = ROLE_MAP[roleId] ?? { label: 'Unknown', bg: 'dark' };
    return <Badge bg={config.bg} className="fs--2">{config.label}</Badge>;
};

// Columns
const makeColumns = (onTerminate, terminating) => [
    {
        accessorKey: 'username',
        header: 'User',
        meta: { headerProps: { className: 'text-900' } },
        cell: ({ row: { original } }) => (
            <div>
                <div className="fw-semibold text-800">
                    {original.last_name}, {original.first_name}
                </div>
                <div className="text-muted fs--2 mb-1">{original.username}</div>
                <RoleBadge roleId={original.role_id} />
            </div>
        ),
    },
    {
        accessorKey: 'group_name',
        header: 'Group',
        meta: { headerProps: { className: 'text-900' } },
        cell: ({ row: { original } }) => (
            <span>{original.group_name ?? <span className="text-muted">—</span>}</span>
        ),
    },
    {
        accessorKey: 'login_source',
        header: 'Device',
        meta: { headerProps: { className: 'text-900' } },
        cell: ({ row: { original } }) => (
            <LoginSourceBadge login_source={original.login_source} />
        ),
    },
    {
        accessorKey: 'ip_address',
        header: 'IP Address',
        meta: { headerProps: { className: 'text-900' } },
        cell: ({ row: { original } }) => (
            <code className="fs--1">{original.ip_address ?? '—'}</code>
        ),
    },
    {
        accessorKey: 'login_time',
        header: 'Login Time',
        meta: { headerProps: { className: 'text-900' } },
        cell: ({ row: { original } }) => (
            <span className="text-nowrap">
                {moment(original.login_time).format('MMM DD, YYYY HH:mm')}
            </span>
        ),
    },
    {
        accessorKey: 'last_activity_at',
        header: 'Last Activity',
        meta: { headerProps: { className: 'text-900' } },
        cell: ({ row: { original } }) => (
            <span
                className="text-nowrap"
                title={moment(original.last_activity_at).format('MMM DD, YYYY HH:mm:ss')}
            >
                {moment(original.last_activity_at).fromNow()}
            </span>
        ),
    },
    {
        accessorKey: 'is_socket_connected',
        header: 'Online',
        meta: {
            headerProps: { className: 'text-900 text-center' },
            cellProps: { className: 'text-center' },
        },
        cell: ({ row: { original } }) => (
            <SocketDot connected={original.is_socket_connected} />
        ),
    },
    {
        accessorKey: 'actions',
        header: 'Action',
        meta: {
            headerProps: { className: 'text-900 text-center' },
            cellProps: { className: 'text-center' },
        },
        cell: ({ row: { original } }) => {
            const isTerminating = terminating === original.session_id;
            return (
                <button
                    className="btn btn-sm btn-outline-danger py-0 px-2 fs--2"
                    disabled={isTerminating}
                    onClick={() => onTerminate(original.session_id, original.username)}
                    title="Terminate Session"
                >
                    {isTerminating ? (
                        <span className="spinner-border spinner-border-sm" />
                    ) : (
                        <>
                            <FontAwesomeIcon icon="times-circle" className="me-1" />
                            Terminate
                        </>
                    )}
                </button>
            );
        },
    },
];

const ViewAllActiveSessionsWithProvider = () => {
    const [allSessions, setAllSessions] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [terminating, setTerminating] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);

    const [roleFilter, setRoleFilter] = useState(ROLE_OPTIONS[0]);
    const [sourceFilter, setSourceFilter] = useState(SOURCE_OPTIONS[0]);
    const [idleFilter, setIdleFilter] = useState(IDLE_OPTIONS[0]);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');

            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/users/all-active-sessions`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const result = data.data ?? [];

            setAllSessions(result);
            setSessions(result);
            setLastRefresh(new Date());

        } catch (err) {
            console.error(err);
            if (!silent) toast.error('Failed to load sessions');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const id = setInterval(() => fetchData(true), REFRESH_INTERVAL);
        return () => clearInterval(id);
    }, [fetchData]);

    useEffect(() => {
        let filtered = [...allSessions];

        if (roleFilter.value) {
            filtered = filtered.filter(s => s.role_id === roleFilter.value);
        }

        if (sourceFilter.value) {
            filtered = filtered.filter(s => s.login_source === sourceFilter.value);
        }

        if (idleFilter.value) {
            filtered = filtered.filter(s => {
                const idle = moment().diff(moment(s.last_activity_at), 'minutes');
                return idle > idleFilter.value;
            });
        }

        setSessions(filtered);
    }, [roleFilter, sourceFilter, idleFilter, allSessions]);

    const handleTerminate = async (sessionId, username) => {
        setTerminating(sessionId);
        try {
            const token = localStorage.getItem('accessToken');

            await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/users/terminate-session`,
                { session_id: sessionId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setAllSessions(prev => prev.filter(s => s.session_id !== sessionId));
            setSessions(prev => prev.filter(s => s.session_id !== sessionId));

            toast.success(`Terminated ${username}`);
        } catch {
            toast.error('Failed');
        } finally {
            setTerminating(null);
        }
    };

    const columns = makeColumns(handleTerminate, terminating);

    const table = useAdvanceTable({
        data: sessions,
        columns,
        sortable: true,
        pagination: true,
    });

    return (
        <AdvanceTableProvider {...table}>
            <CommonBreadCrumb
                breadcrumbItems={[
                    { name: 'Dashboard', href: '/main' },
                    { name: 'Active Sessions', href: paths.activeSessions, isActive: true },
                ]}
            />

            <Card>
                <Card.Header className="d-flex align-items-center justify-content-between py-2">
                    <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon="users" className="text-primary" />
                        <span className="fw-semibold text-800">Active Sessions</span>
                        <Badge bg="primary" pill>{sessions.length}</Badge>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {lastRefresh && (
                            <span className="text-muted fs--2">
                                Refreshed {moment(lastRefresh).format('HH:mm:ss')}
                            </span>
                        )}
                        <button
                            className="btn btn-sm btn-outline-primary py-0 px-2 fs--2"
                            onClick={() => fetchData()}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon="sync-alt" spin={loading} className="me-1" />
                            Refresh
                        </button>
                    </div>
                </Card.Header>

                <Card.Body>
                    <Row className="mb-3">
                        <Col md={3}>
                            <AdvanceTableSearchBox />
                        </Col>

                        <Col md={2}>
                            <ThemedSelect options={ROLE_OPTIONS} value={roleFilter} onChange={setRoleFilter} />
                        </Col>

                        <Col md={2}>
                            <ThemedSelect options={SOURCE_OPTIONS} value={sourceFilter} onChange={setSourceFilter} />
                        </Col>

                        <Col md={2}>
                            <ThemedSelect options={IDLE_OPTIONS} value={idleFilter} onChange={setIdleFilter} />
                        </Col>
                    </Row>

                    {loading ? <ShimmerUI/> : <AdvanceTable
                        headerClassName="bg-200 text-nowrap align-middle text-900"
                        rowClassName="align-middle white-space-nowrap"
                        tableProps={{
                            size: 'sm',
                            striped: true,
                            className: 'fs-10 mb-0 overflow-hidden',
                        }} />}
                </Card.Body>

                <Card.Footer>
                    <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                </Card.Footer>
            </Card>
        </AdvanceTableProvider>
    );
};

export default ViewAllActiveSessionsWithProvider;
